import { motion } from 'framer-motion';
import { X, CheckCircle2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FounderAvatar from '@/components/brand/FounderAvatar';
import SocialRow from '@/components/brand/SocialRow';
import StoreBadges from '@/components/brand/StoreBadges';
import { FOUNDER } from '@/lib/brand';
import { useLocalized } from '@/hooks/useLocalized';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface Props {
    onClose: () => void;
    onRestart: () => void;
}

const ThankYouScreen = ({ onClose, onRestart }: Props) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const founderName = useLocalized({ ar: FOUNDER.nameAr, en: FOUNDER.nameEn });

    return (
        <div
            className={cn(
                'rounded-[28px] shadow-2xl shadow-black/50 overflow-hidden flex flex-col relative',
                'h-[min(600px,calc(100dvh-7.5rem))] sm:h-[min(600px,calc(100dvh-8rem))]',
                isMobile ? 'bg-card border border-white/10' : 'glass-v2-strong',
                'overscroll-contain',
            )}
        >
            {/* Top accent line */}
            <div
                aria-hidden
                className="absolute top-0 inset-x-8 h-px opacity-60 pointer-events-none"
                style={{
                    background:
                        'linear-gradient(90deg, transparent, hsl(260 90% 65%), hsl(190 90% 50%), transparent)',
                }}
            />

            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between relative">
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-50"
                    style={{
                        background:
                            'radial-gradient(320px circle at 50% 0%, hsl(160 80% 50% / 0.18), transparent 70%)',
                    }}
                />
                <div className="flex items-center gap-2 relative">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <h3 className="font-v2-display text-sm text-foreground">
                        {t('chat.thanks.header')}
                    </h3>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors text-muted-foreground hover:text-foreground min-w-[40px] min-h-[40px] flex items-center justify-center relative"
                    aria-label={t('chat.aria_close')}
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-8 flex flex-col items-center text-center custom-scrollbar">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', bounce: 0.5 }}
                    className="mb-5 shadow-xl shadow-primary/30 rounded-full"
                >
                    <FounderAvatar size={80} ringBorder />
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="font-v2-display text-2xl mb-1"
                >
                    <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                        {t('chat.thanks.headline')}
                    </span>
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="text-sm text-muted-foreground mb-6 max-w-xs leading-relaxed"
                >
                    {t('chat.thanks.body', { name: founderName })
                        .split(founderName)
                        .reduce<(string | JSX.Element)[]>((acc, part, idx, arr) => {
                            acc.push(part);
                            if (idx < arr.length - 1) {
                                acc.push(
                                    <span key={idx} className="text-foreground font-bold">
                                        {founderName}
                                    </span>,
                                );
                            }
                            return acc;
                        }, [])}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="w-full mb-6"
                >
                    <p className="font-v2-label text-[10px] text-white/60 mb-3">
                        {t('chat.thanks.follow_founder')}
                    </p>
                    <SocialRow variant="cta" channels={['linkedin', 'telegram']} className="mb-3" />
                    <SocialRow variant="pill" channels={['youtube', 'facebook']} />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45 }}
                    className="w-full"
                >
                    <p className="font-v2-label text-[10px] text-white/60 mb-3">
                        {t('chat.thanks.try_apps')}
                    </p>
                    <StoreBadges size="sm" />
                </motion.div>
            </div>

            <div
                className="p-3 border-t border-white/10"
                style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            >
                <button
                    type="button"
                    onClick={onRestart}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-foreground hover:bg-white/10 hover:border-white/20 font-bold text-sm transition-all flex items-center justify-center gap-2 min-h-[48px]"
                >
                    <RefreshCw className="w-4 h-4" />
                    {t('chat.thanks.restart')}
                </button>
            </div>
        </div>
    );
};

export default ThankYouScreen;
